import React, { useMemo, useState } from 'react';
import { HbrDomTable, HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { checkCircle } from '@harbor/elements/icons';

import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { useOrgChart } from '../../../hooks/useOrgChart';
import { useUserJourneyMapDetail } from '../../../hooks/enablement-journey-map-hooks';
import type { EducationDrillNode } from '../../../hooks/education-completion-hooks';
import type { OrgChartRow } from '../../../support/orgScope';
import {
  categorizeItems,
  round2,
} from '../../../support/enablementLeader';
import { TileRuntimeContext } from '../../../support/tileRuntimeContext';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import LeaderConfigDashboard from '../LeaderConfigDashboard';
import CampaignChartCard, { ChartGrid, KpiCards } from '../Campaigns/CampaignChartCard';
import EducationSeriesTable, { formatIsoDate, seriesRowFor } from './EducationSeriesTable';
import { inWindow, windowFromFilter } from '../../../support/enablementWindow';

/**
 * Leader > Enablement > Individual. One person's enablement in detail: their Mindtickle education
 * completion (config-driven tiles + per-series tables) and journey maps (KPIs + charts + table). The
 * charts are DB-driven custom tiles; the per-series and per-map tables remain hard-coded item-grain
 * detail. Education tiles degrade to an "unavailable locally" note when the node is null.
 */

const COMPONENT_KEY = 'Leader.EnablementIndividualContributorPage';

interface Props {
  email: string;
  /** This person's education drill node (own detail + rollup); null when unavailable (local mode). */
  eduNode: EducationDrillNode | null;
  /** Enablement time dropdown as a `time_filter` token; exposed to the tiles as `{education_time_filter}`. */
  timeFilter?: string;
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function EnablementIndividualContributorPage({ email, eduNode, timeFilter }: Props) {
  const [isInformationalOpen, setInformationalOpen] = useState(true);
  const [isSelfTrainingOpen, setSelfTrainingOpen] = useState(true);
  const [isJourneyOpen, setJourneyOpen] = useState(true);

  const owner = String(email || '').toLowerCase();
  const { data: orgRows } = useOrgChart();

  const userId = useMemo(() => {
    const row = ((orgRows as OrgChartRow[]) ?? []).find(
      (r) => String(r.user_email || '').toLowerCase() === owner,
    );
    return row?.user_id ?? null;
  }, [orgRows, owner]);

  const runtimeContext: TileRuntimeContext = useMemo(() => {
    return {
      email: owner,
      managerEmail: owner,
      domain: 'enablement',
      teamScope: [],
      teamSize: 0,
      teamEmailsList: '',
      viewMode: 'individual',
      isTeamView: false,
      isIndividualView: true,
      education_time_filter: timeFilter,
    };
  }, [owner, timeFilter]);

  const node = eduNode;
  const hasEducation = !!node;

  // Per-series items grouped by scope bucket (shown required, then informational, then self-enrolled).
  // "Required" = ADMIN-invited (the assigned enablement counted in "X of X"); "Self-enrolled" = SELF-
  // invited training the IC took on their own, surfaced in its own section below.
  // REFINEMENT (a): categorizeItems now filters out closed series (closed === true).
  const buckets = useMemo(() => categorizeItems(node?.own?.items), [node]);
  // Shown in this order: Required (the set the "X of X" figure is drawn from), Informational, then Self-enrolled.
  const requiredRows = useMemo(() => buckets.required.map(seriesRowFor('Required')), [buckets]);
  // Informational series are only listed once the person has engaged with them: completed or in progress.
  const informationalRows = useMemo(
    () => buckets.informational.filter((it) => it.status !== 'not_started').map(seriesRowFor('Informational')),
    [buckets],
  );
  // REFINEMENT (c): check if all required series are completed for the "All caught up" note.
  const allRequiredCompleted = useMemo(
    () => buckets.required.length === 0 || buckets.required.every((it) => it.status === 'completed'),
    [buckets],
  );
  // Self-directed training the IC enrolled in themselves (SELF invite) — never counted toward "X of X".
  const selfDirectedRows = useMemo(() => buckets.self.map(seriesRowFor('Self-enrolled')), [buckets]);
  const selfDirectedCompleted = useMemo(
    () => buckets.self.filter((it) => it.status === 'completed').length,
    [buckets],
  );

  const { maps: allMaps, isLoading: loadingJourney } = useUserJourneyMapDetail(userId);

  // The Time Period dropdown places a map like it places a series: by its due date, else its assigned date.
  const maps = useMemo(() => {
    const window = windowFromFilter(timeFilter);
    return allMaps.filter((m) => inWindow(window, m.dueDate, m.assignedDate));
  }, [allMaps, timeFilter]);
  const assignedCount = maps.length;
  const completedCount = useMemo(() => maps.filter((m) => m.done).length, [maps]);

  const inProgressMaps = useMemo(
    () => maps.filter((m) => !m.done && (Number(m.percent) || 0) > 0).length,
    [maps],
  );
  const requiredMaps = useMemo(() => maps.filter((m) => m.isRequired === true), [maps]);
  const optionalMaps = useMemo(() => maps.filter((m) => m.isRequired !== true), [maps]);
  const avgMapProgress = useMemo(() => {
    if (!maps.length) return 0;
    const total = maps.reduce((acc, m) => acc + (Number(m.percent) || 0), 0);
    return round2(total / maps.length);
  }, [maps]);

  const journeyKpis = [
    { label: 'Maps assigned', value: assignedCount },
    { label: 'Maps completed', value: completedCount },
    { label: 'In progress', value: inProgressMaps },
    { label: 'Required done', value: `${requiredMaps.filter((m) => m.done).length} of ${requiredMaps.length}` },
    { label: 'Optional done', value: `${optionalMaps.filter((m) => m.done).length} of ${optionalMaps.length}` },
    { label: 'Avg progress', value: `${avgMapProgress}%` },
  ];

  // Completion % per assigned map — flat [{label,value}] contract for the inline bar tile.
  const journeyProgressRows = useMemo(
    () =>
      maps.map((m) => ({
        label: m.mapName,
        value: round2(Number(m.percent) || 0),
      })),
    [maps],
  );

  // Assigned maps bucketed by status — flat [{label,value}] contract for the inline donut tile.
  const journeyStatusDonut = useMemo(() => {
    const complete = maps.filter((m) => m.done).length;
    const inProgress = maps.filter((m) => !m.done && (Number(m.percent) || 0) > 0).length;
    const notStarted = maps.length - complete - inProgress;
    return [
      { label: 'Complete', value: complete },
      { label: 'In progress', value: inProgress },
      { label: 'Not started', value: notStarted },
    ].filter((d) => d.value > 0);
  }, [maps]);

  const visibleColumns: Column[] = useMemo(() => {
    const col = (id: string, name: string, width: number, align = ColumnAlignment.Left): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    return [
      col('map', 'Journey Map', 300),
      col('type', 'Type', 90),
      col('items', 'Items', 110, ColumnAlignment.Right),
      col('percent', 'Progress', 100, ColumnAlignment.Right),
      col('status', 'Status', 110),
      col('assigned', 'Assigned', 110),
      col('due', 'Due Date', 110),
      col('completed', 'Completed', 110),
    ]
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const rows = useMemo(
    () =>
      maps.map((m) => ({
        id: String(m.assignmentId),
        map: m.mapName,
        type: m.isRequired === true ? 'Required' : 'Optional',
        items: `${m.completedItems} of ${m.totalItems}`,
        percent: m.percent != null ? `${round2(m.percent)}%` : '—',
        status: m.done
          ? 'Complete'
          : (Number(m.percent) || 0) > 0 || m.completedItems > 0
            ? 'In progress'
            : 'Not started',
        assigned: formatIsoDate(m.assignedDate) ?? '—',
        due: formatIsoDate(m.dueDate) ?? '—',
        completed: fmtDate(m.completedDate),
      })),
    [maps],
  );

  const renderHeader = () =>
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div className="w-max">{column.name}</div>
      </div>
    ));

  const renderBody = () => {
    if (!rows.length) return null;
    const list: JSX.Element[] = [];
    for (const row of rows) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;
        const align =
          column.id === 'items' || column.id === 'percent' ? 'flex-end' : 'flex-start';
        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: align, fontVariantNumeric: 'tabular-nums' }}
          >
            {(row as any)[column.id]}
          </div>,
        );
      }
    }
    return list;
  };

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      {!hasEducation ? (
        <StbAccordion variant="minimal" label="Education Completion" isOpen={true} toggleIsOpen={() => {}}>
          <div style={{ padding: 12 }}>
            <div className="hbr-css__text-secondary-sm">
              Education completion is unavailable in this environment (Mindtickle/Snowflake not
              connected). Journey-map completion is shown below.
            </div>
          </div>
        </StbAccordion>
      ) : (
        <>
          <LeaderConfigDashboard
            componentKey={COMPONENT_KEY}
            regionKey="dashboard_education"
            runtimeContext={runtimeContext}
            fallbackLabel="Education Completion"
            fallbackColumns={3}
          />

          <LeaderConfigDashboard
            componentKey={COMPONENT_KEY}
            regionKey="dashboard_education_required"
            runtimeContext={runtimeContext}
            fallbackLabel="Required Education"
            fallbackColumns={2}
          />

          <StbAccordion
            variant="minimal"
            label="Required Education Detail"
            isOpen={true}
            toggleIsOpen={() => {}}
          >
            <div style={{ padding: 12 }}>
              {allRequiredCompleted ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12 }}>
                  <HbrIcon asset={checkCircle} sentiment="success" />
                  <span className="hbr-css__text-primary-sm">
                    All caught up — no open required series.
                  </span>
                </div>
              ) : (
                <EducationSeriesTable rows={requiredRows} />
              )}
            </div>
          </StbAccordion>
        </>
      )}

      {hasEducation && informationalRows.length > 0 && (
        <>
          <LeaderConfigDashboard
            componentKey={COMPONENT_KEY}
            regionKey="dashboard_education_informational"
            runtimeContext={runtimeContext}
            fallbackLabel="Informational Education"
            fallbackColumns={2}
          />

          <StbAccordion
            variant="minimal"
            label="Informational Education Detail"
            isOpen={isInformationalOpen}
            toggleIsOpen={setInformationalOpen}
          >
            <div style={{ padding: 12 }}>
              <div className="hbr-css__text-secondary-sm" style={{ marginBottom: 8 }}>
                Assigned series that are not part of the required set and do not count toward its completion figure. Series not yet started are not listed.
              </div>
              <EducationSeriesTable rows={informationalRows} />
            </div>
          </StbAccordion>
        </>
      )}

      {hasEducation && selfDirectedRows.length > 0 && (
        <>
          <LeaderConfigDashboard
            componentKey={COMPONENT_KEY}
            regionKey="dashboard_education_self"
            runtimeContext={runtimeContext}
            fallbackLabel="Self-Enrolled Education"
            fallbackColumns={2}
          />

          <StbAccordion
            variant="minimal"
            label="Self-Enrolled Education Detail"
            isOpen={isSelfTrainingOpen}
            toggleIsOpen={setSelfTrainingOpen}
          >
            <div style={{ padding: 12 }}>
              <div className="hbr-css__text-secondary-sm" style={{ marginBottom: 8 }}>
                Series {owner} enrolled in on their own (Mindtickle SELF invites). These do not count toward
                the required “{selfDirectedRows.length}” completion figure — {selfDirectedCompleted} completed.
              </div>
              <EducationSeriesTable rows={selfDirectedRows} />
            </div>
          </StbAccordion>
        </>
      )}

      <StbAccordion
        variant="minimal"
        label="Journey Maps"
        isOpen={isJourneyOpen}
        toggleIsOpen={setJourneyOpen}
      >
        <div style={{ minHeight: 200, padding: 12 }}>
          {loadingJourney ? (
            <HbrSpinner>Loading journey maps…</HbrSpinner>
          ) : rows.length === 0 ? (
            <div className="hbr-css__text-secondary-sm">
              {allMaps.length > 0
                ? `None of ${owner}’s ${allMaps.length} journey maps fall in this period.`
                : `No journey maps assigned to ${owner}.`}
            </div>
          ) : (
            <>
              <KpiCards kpis={journeyKpis} />
              <ChartGrid>
                <CampaignChartCard
                  title="Progress by map"
                  subtitle="Completion % per assigned map"
                  chartType="horizontal_bar_chart"
                  data={journeyProgressRows}
                  tileKey={`enb-ic-${owner}-jmprogress`}
                  tooltipKeys={{ label: 'Journey map', value: 'Progress %' }}
                  settings={{
                    axisBottomLegend: 'Progress %',
                    axisLeftLegend: 'Journey map',
                    valueFormat: 'number',
                    axisLeftLegendOffset: -96,
                    marginLeft: 112,
                    maxLeftMarginRatio: 0.5,
                  }}
                  empty="No assigned journey maps."
                />
                <CampaignChartCard
                  title="Map status"
                  subtitle="Assigned maps by status"
                  chartType="donut_chart"
                  data={journeyStatusDonut}
                  tileKey={`enb-ic-${owner}-jmstatus`}
                  tooltipKeys={{ label: 'Status', value: 'Maps' }}
                  empty="No assigned journey maps."
                />
              </ChartGrid>
              <div style={{ marginTop: 12 }}>
                <HbrDomTable autoLayout={true} columns={visibleColumns} data={rows}>
                  {renderHeader()}
                  {renderBody()}
                </HbrDomTable>
              </div>
            </>
          )}
        </div>
      </StbAccordion>
    </div>
  );
}
